/**
 * NOS-90: Section catalog popup for adding sections between existing sections.
 * All values from docs/BUILDER_DESIGN_SPEC.md Section 6.
 */
import type { PageSection } from "@/templates/types"

/** Section templates per BUILDER_DESIGN_SPEC Section 17 */
const SECTION_TEMPLATES = [
  { type: "hero", label: "Hero" },
  { type: "image_text", label: "Image + Text" },
  { type: "impact_stats", label: "Impact Stats" },
  { type: "donation_cta", label: "Donation CTA" },
  { type: "donation_form", label: "Donation Form" },
  { type: "campaign_banner", label: "Campaign Banner" },
  { type: "team_members", label: "Team Members" },
  { type: "testimonials", label: "Testimonials" },
  { type: "volunteer_signup", label: "Volunteer Signup" },
  { type: "gallery", label: "Gallery" },
  { type: "accordion", label: "Accordion" },
  { type: "contact", label: "Contact" },
  { type: "footer", label: "Footer" },
] as const

export interface SectionCatalogProps {
  sections: PageSection[]
  primaryColor: string
  gapAfterIndex: number
  onSelect: (sectionType: string, afterIndex: number) => void | Promise<void>
  onClose: () => void
}

export function SectionCatalog({
  sections,
  primaryColor: _primaryColor,
  gapAfterIndex,
  onSelect,
  onClose,
}: SectionCatalogProps) {
  const hasHero = sections.some((s) => s.section_type === "hero")
  const hasFooter = sections.some((s) => s.section_type === "footer")

  const visibleTemplates = SECTION_TEMPLATES.filter((t) => {
    if (t.type === "hero" && hasHero) return false
    if (t.type === "footer" && hasFooter) return false
    return true
  })

  const handleSelect = async (type: string) => {
    // Footer always inserts at second-to-last (BUILDER_DESIGN_SPEC)
    const afterIndex =
      type === "footer" ? Math.max(0, sections.length - 2) : gapAfterIndex
    try {
      await onSelect(type, afterIndex)
    } finally {
      onClose()
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Add section"
      style={{
        position: "absolute",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(12,12,16,0.97)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 4,
        padding: 8,
        width: 340,
        minWidth: 340,
        zIndex: 100,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {visibleTemplates.map(({ type, label }) => (
        <button
          key={type}
          type="button"
          onClick={() => handleSelect(type)}
          style={{
            padding: "10px 12px",
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.05)",
            borderRadius: 7,
            color: "rgba(255,255,255,0.6)",
            fontSize: 10,
            fontWeight: 500,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {label}
        </button>
      ))}
    </div>
  )
}
